import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Full US states list (alphabetized)
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Quiz questions with scoring logic
const QUESTIONS = [
  {
    id: 'injury',
    q: 'Were you injured in the accident?',
    why: 'We help those with physical injuries from car accidents. Injuries strengthen your claim.',
    options: [
      { label: 'Yes', value: 'yes', score: 5, disqualify: false },
      { label: 'No', value: 'no', score: -5, disqualify: true }
    ]
  },
  {
    id: 'timeframe',
    q: 'When did the accident occur?',
    why: 'Recent accidents within statute of limitations qualify. Timing affects claim validity.',
    options: [
      { label: 'Within the last 6 months', value: '6mo', score: 5, disqualify: false },
      { label: '6 months to 1 year ago', value: '1yr', score: 4, disqualify: false },
      { label: '1-2 years ago', value: '2yr', score: 3, disqualify: false },
      { label: 'More than 2 years ago', value: '2yr+', score: 1, disqualify: false }
    ]
  },
  {
    id: 'fault',
    q: 'Who was at fault for the accident?',
    why: 'If another driver was at fault, you may qualify for compensation from their insurance.',
    options: [
      { label: 'Other driver', value: 'other', score: 5, disqualify: false },
      { label: 'Shared fault', value: 'shared', score: 2, disqualify: false },
      { label: 'Unsure', value: 'unsure', score: 1, disqualify: false },
      { label: 'I was at fault', value: 'me', score: -3, disqualify: false }
    ]
  },
  {
    id: 'state',
    q: 'In which state did the accident occur?',
    why: 'State laws vary for accident claims. We match you with local legal experts.',
    type: 'select',
    options: US_STATES.map(state => ({ label: state, value: state.toLowerCase().replace(/\s+/g, '-'), score: 0, disqualify: false }))
  },
  {
    id: 'medical',
    q: 'Did you receive medical treatment?',
    why: 'Medical records document your injuries and support your claim for compensation.',
    options: [
      { label: 'Yes, hospitalized', value: 'hospital', score: 5, disqualify: false },
      { label: 'Yes, outpatient care', value: 'outpatient', score: 4, disqualify: false },
      { label: 'Yes, minor treatment', value: 'minor', score: 2, disqualify: false },
      { label: 'No, but planning to', value: 'planning', score: 1, disqualify: false },
      { label: 'No treatment', value: 'none', score: -2, disqualify: false }
    ]
  },
  {
    id: 'insurance',
    q: 'Was insurance involved?',
    why: 'Insurance claims help establish the accident record and strengthen your case.',
    options: [
      { label: 'Yes, claim filed', value: 'filed', score: 3, disqualify: false },
      { label: 'Yes, but no claim yet', value: 'not-filed', score: 2, disqualify: false },
      { label: 'No insurance', value: 'none', score: 0, disqualify: false }
    ]
  },
  {
    id: 'ongoing',
    q: 'Are you still experiencing pain or issues?',
    why: 'Ongoing pain or issues increase claim value and urgency for legal help.',
    options: [
      { label: 'Yes, significant pain', value: 'significant', score: 5, disqualify: false },
      { label: 'Yes, some discomfort', value: 'some', score: 3, disqualify: false },
      { label: 'Mostly recovered', value: 'recovered', score: 1, disqualify: false },
      { label: 'Fully recovered', value: 'fully', score: 0, disqualify: false }
    ]
  }
];

function Quiz() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);

  const question = QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
  const canGoBack = currentQuestion > 0;

  const handleAnswer = (option) => {
    const newAnswers = {
      ...answers,
      [question.id]: option.value
    };
    setAnswers(newAnswers);

    const newScore = score + (option.score || 0);
    setScore(newScore);

    // Check disqualification
    if (option.disqualify) {
      sessionStorage.setItem('disqualifyReason', question.id);
      navigate('/disqualified');
      return;
    }

    // Move to next question or complete
    if (currentQuestion < QUESTIONS.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        setShowTooltip(false);
      }, 300);
    } else {
      completeQuiz(newAnswers, newScore);
    }
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;

    const selectedOption = question.options.find(opt => opt.value === selectedValue);
    handleAnswer(selectedOption);
  };

  const completeQuiz = (finalAnswers, finalScore) => {
    // Normalize score to 0-10 scale
    const maxPossibleScore = 28; // Sum of all max scores (5+5+5+0+5+3+5)
    const normalizedScore = Math.max(0, Math.min(10, (finalScore / maxPossibleScore) * 10));

    // Store in sessionStorage
    sessionStorage.setItem('quizAnswers', JSON.stringify(finalAnswers));
    sessionStorage.setItem('quizScore', normalizedScore.toFixed(1));
    sessionStorage.setItem('quizCompleted', 'true');

    // Navigate to form
    setTimeout(() => {
      navigate('/form');
    }, 300);
  };

  const handleBack = () => {
    if (canGoBack) {
      const prevQuestion = QUESTIONS[currentQuestion - 1];
      const prevAnswer = answers[prevQuestion.id];

      // Subtract previous score if exists
      if (prevAnswer) {
        const prevOption = prevQuestion.options.find(opt => opt.value === prevAnswer);
        if (prevOption) {
          setScore(score - (prevOption.score || 0));
        }
      }

      // Remove answer
      const newAnswers = { ...answers };
      delete newAnswers[question.id];
      setAnswers(newAnswers);

      setCurrentQuestion(currentQuestion - 1);
      setShowTooltip(false);
    }
  };

  return (
    <div className="container">
      {/* Hero Section - Above the fold */}
      <div className="quiz-hero text-center">
        <h1 className="quiz-headline">
          <span style={{marginRight: '8px'}}>🚗</span>
          In a Car Accident? Qualify for Cash Settlement Now
        </h1>
        <p className="quiz-subheadline">Quick 1-min quiz—free consultation</p>
      </div>

      {/* Progress Bar */}
      <div className="progress-container" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <span className="progress-text">
          Question {currentQuestion + 1} of {QUESTIONS.length}
        </span>
      </div>

      {/* Question Card */}
      <div className="card question-card">
        <div className="card-header">
          <h2 className="question-title">{question.q}</h2>

          {/* Tooltip */}
          {question.why && (
            <div className="question-tooltip">
              <button
                className="tooltip-trigger"
                onClick={() => setShowTooltip(!showTooltip)}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                aria-label="Why we ask this"
                type="button"
              >
                <span className="tooltip-icon">?</span>
              </button>
              {showTooltip && (
                <div className="tooltip-content" role="tooltip">
                  {question.why}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card-body">
          {/* Render based on question type */}
          {question.type === 'select' ? (
            <div className="form-group">
              <select
                className="form-select question-select"
                onChange={handleSelectChange}
                defaultValue=""
                aria-label={question.q}
              >
                <option value="" disabled>Select a state...</option>
                {question.options.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="question-options">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  className="btn btn-primary question-btn"
                  onClick={() => handleAnswer(option)}
                  aria-label={`Select ${option.label}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Back Button */}
        {canGoBack && (
          <div className="card-footer">
            <button
              className="btn-back"
              onClick={handleBack}
              aria-label="Go back to previous question"
            >
              ← Back
            </button>
          </div>
        )}
      </div>

      {/* Trust Indicators */}
      <div className="quiz-trust text-center">
        <p className="trust-text">
          <span className="trust-icon">🔒</span> Your information is secure and confidential
        </p>
        <p className="trust-text">
          <span className="trust-icon">⚡</span> Average response time: Under 2 hours
        </p>
      </div>
    </div>
  );
}

export default Quiz;
